<template>
  <div>
    <v-btn
      rounded
      size="small"
      class="primary-inverted-button"
      @click="onAdd"
    >
      <v-icon
        tag="span"
        start
      >
        mdi-plus
      </v-icon>
      {{ $t("commonStrings.add") }}
    </v-btn>
    <v-dialog
      :key="dimension.id"
      v-model="display"
      max-width="500"
      persistent
    >
      <v-card loading="isSaving">
        <v-card-title>{{ title }}</v-card-title>
        <v-card-text>
          <vee-form ref="financialDimensionObserver">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <vee-field
                    v-slot="{ componentField, errors }"
                    v-model="dimension.categoryType"
                    rules="required"
                    :name="
                      $t(
                        'financialDimensionStrings.dimensionsTable.headers.category'
                      )
                    "
                  >
                    <v-select
                      v-bind="componentField"
                      density="compact"
                      variant="outlined"
                      :items="dimensionCategories"
                      :label="
                        $t(
                          'financialDimensionStrings.dimensionsTable.headers.category'
                        )
                      "
                      :error-messages="errors"
                    />
                  </vee-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <vee-field
                    v-slot="{ componentField, errors }"
                    v-model="dimension.code"
                    :rules="{
                      max: 5,
                      required: true,
                      is_not: usedCodesInCategory
                    }"
                    :name="
                      $t(
                        'financialDimensionStrings.dimensionsTable.headers.code'
                      )
                    "
                  >
                    <integer-text-field
                      v-bind="componentField"
                      :error-messages="errors"
                      :label="
                        $t(
                          'financialDimensionStrings.dimensionsTable.headers.code'
                        )
                      "
                    />
                  </vee-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <vee-field
                    v-slot="{ componentField, errors }"
                    v-model="dimension.name"
                    rules="max:50|required"
                    :name="
                      $t(
                        'financialDimensionStrings.dimensionsTable.headers.name'
                      )
                    "
                  >
                    <v-text-field
                      v-bind="componentField"
                      :error-messages="errors"
                      :label="
                        $t(
                          'financialDimensionStrings.dimensionsTable.headers.name'
                        )
                      "
                      counter="50"
                      variant="outlined"
                      density="compact"
                    />
                  </vee-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-checkbox
                    v-model="dimension.active"
                    :label="
                      $t(
                        'financialDimensionStrings.dimensionsTable.headers.active'
                      )
                    "
                  />
                </v-col>
              </v-row>
            </v-container>
          </vee-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            rounded
            class="primary-inverted-button mb-2 mb-sm-0"
            :block="xs"
            size="small"
            :disabled="isSaving"
            :loading="isSaving"
            @click="cancel"
          >
            {{ $t("commonStrings.cancel") }}
          </v-btn>
          <v-btn
            rounded
            class="primary-button ml-0 ml-sm-2"
            :block="xs"
            size="small"
            :disabled="isSaving"
            :loading="isSaving"
            @click="save"
          >
            {{ $t("commonStrings.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { compare } from "fast-json-patch";
import { mapGetters } from "vuex";
import { Form as VeeForm, Field as VeeField } from "vee-validate";
import { api, urls } from "@/utils/api";
import { copy } from "@/utils/object";
import IntegerTextField from "@/views/common/components/IntegerTextField";
import { handleError } from "@/utils/errorHandler";
import { useDisplay } from 'vuetify';
import { useSnacks } from '@/composables/snacks';

export default {
  name: "FinancialDimensionEditDialog",
  components: {
    VeeForm,
    VeeField,
    IntegerTextField
  },
  props: {
    dimension: {
      required: false
    },
    dimensionCategories: {
      required: true
    },
    otherDimensions: {
      required: true
    }
  },
  setup() {
    const { xs } = useDisplay();
    const { addSnackbar } = useSnacks();
    return { xs, addSnackbar };
  },
  data() {
    return {
      display: false,
      initial: {}
    };
  },
  methods: {
    onAdd() {
      this.$emit("addNew");
    },
    async save() {
      const isDataValid = await this.$refs.financialDimensionObserver.validate();

      if (!isDataValid) {
        this.showValidationErrorSnackbar(this.$refs.financialDimensionObserver);
        return;
      }

      if (this.dimension.id) {
        await this.patchExisting();
      } else {
        await this.postNew();
      }
    },
    async postNew() {
      try {
        const response = await api.v1.financialDimensions.post(this.dimension);

        if (response.status === 200) {
          this.successResponse();
        }
      } catch (error) {
        await handleError(error);
      }
    },
    async patchExisting() {
      try {
        const patchDoc = compare(this.initial, this.dimension);
        const response = await api.v1.financialDimensions.patch(
          this.dimension.id,
          patchDoc
        );
        if (response.status === 204) {
          this.successResponse();
        }
      } catch (error) {
        await handleError(error);
      }
    },
    successResponse() {
      this.addSnackbar(this.$t("financialDimensionStrings.dimensionsTable.editDialog.saved"),
        "success",
        true
      );
      this.$emit("saved");
    },
    cancel() {
      this.$emit("cancelled");
    }
  },
  computed: {
    ...mapGetters({
      urlState: "utilsRestStore/urlState"
    }),
    isPosting() {
      return this.urlState(urls.v1.financialDimensions.post());
    },
    isPatching() {
      return this.urlState(
        urls.v1.financialDimensions.patch(this.dimension.id)
      );
    },
    isSaving() {
      return this.isPosting || this.isPatching;
    },
    title() {
      if (this.dimension.id <= 0) {
        return this.$t("financialDimensionStrings.dimensionsTable.editDialog.createTitle");
      }
      return this.$t("financialDimensionStrings.dimensionsTable.editDialog.editTitle");
    },
    usedCodesInCategory() {
      const dimensionsInCateogry = this.otherDimensions.filter(
        x => x.categoryType === this.dimension.categoryType
      );
      const usedCodes = dimensionsInCateogry.map(x => x.code);
      return usedCodes;
    }
  },
  watch: {
    dimension: {
      handler(val) {
        if (val.id >= 0) {
          this.display = !!val;
        } else {
          this.display = false;
        }
      },
      deep: true
    },
    display(val) {
      if (val) {
        this.initial = copy(this.dimension);
      }
    }
  }
};
</script>
